import type {
  ComparisonFilters,
  ComparisonRelation,
  ComparisonSource,
} from "../../../shared/contracts/product-pages.js";
import { useEffect, useState } from "react";

import { displaySides } from "./comparison-presentation.js";
import { insightQueryKeys } from "./insight-navigation.js";
import { rivalryCalendarSearch } from "./use-rivalry-calendar.js";
import {
  comparisonAnalyticsLabel,
  writeRivalryCalendar,
  type RivalryCalendarSelection,
} from "../../../shared/presentation/rivalry-calendar.js";
export { teePairLabel } from "./comparison-tee-label.js";
export { comparisonQuery } from "./comparison-query.js";

export function oppositeParty(relation: ComparisonRelation) {
  return relation.viewerSide === "left" ? relation.right : relation.left;
}

export function relationLabel(relation: ComparisonRelation): string {
  return displaySides(relation)
    .map((side) => relation[side].name)
    .join(" vs ");
}

export function sourceDate(source: ComparisonSource): string {
  if (!source.effectiveAt) return "Date not recorded";
  const date = new Date(
    source.calendarDate
      ? `${source.calendarDate}T12:00:00Z`
      : source.effectiveAt,
  );
  if (Number.isNaN(date.getTime())) return "Date not recorded";
  return `${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}${source.dateSource === "completed-at" ? " · completion date" : ""}`;
}

export function selectionSummary(filters: ComparisonFilters): string {
  return `${comparisonAnalyticsLabel(filters)} · ${filters.basis === "net" ? "Net" : "Gross"}${Object.entries(filters).some(([key, value]) => value !== undefined && !["basis", "period", "year", "ledger", "environment"].includes(key)) ? " · Filtered" : ""}`;
}

export function initialRelationId(): string | null {
  return new URLSearchParams(window.location.search).get("relation");
}

export function useSelectedRelation() {
  const [selectedId, setSelectedId] = useState(initialRelationId);
  useEffect(() => {
    const update = () => setSelectedId(initialRelationId());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return [selectedId, setSelectedId] as const;
}

export function rememberRelation(id: string | null): void {
  const url = new URL(window.location.href);
  for (const key of insightQueryKeys) url.searchParams.delete(key);
  for (const [key, value] of new URLSearchParams(rivalryCalendarSearch()))
    url.searchParams.set(key, value);
  if (id) url.searchParams.set("relation", id);
  else url.searchParams.delete("relation");
  window.history.replaceState(window.history.state, "", url);
}

export function openSharedRelation(
  id: string,
  selection?: RivalryCalendarSelection,
): void {
  const query = new URLSearchParams(rivalryCalendarSearch());
  if (selection) writeRivalryCalendar(query, selection);
  query.set("relation", id);
  window.history.pushState(null, "", `/rivalry?${query}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
  document.querySelector(".fr-main")?.scrollTo({ top: 0 });
}
